import { useState, useEffect, useRef } from 'react';
import logger from '../utils/logger';

/**
 * Custom hook for managing comment ratings
 * Handles loading, updating, and persisting feedback ratings
 */
export function useFeedbackRatings(taskId, authToken, API_BASE_URL) {
  const [commentRatings, setCommentRatings] = useState({});
  const hasLoadedRatings = useRef(false);

  // Load existing ratings when component mounts
  useEffect(() => {
    const loadExistingRatings = async () => {
      if (!taskId || hasLoadedRatings.current) return;
      
      try {
        logger.info('Loading existing ratings for taskId:', taskId);
        const response = await fetch(
          `${API_BASE_URL}/api/results/${taskId}/ratings`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.ratings) {
            logger.info('Loaded existing ratings:', data.ratings);
            setCommentRatings(data.ratings);
          }
        }
        hasLoadedRatings.current = true;
      } catch (error) {
        logger.error('Error loading ratings:', error);
      }
    };
    
    loadExistingRatings();
  }, [taskId, authToken, API_BASE_URL]);

  // Handle rating a comment
  const handleCommentRating = async (paraIndex, fbIndex, rating) => {
    if (!taskId) {
      logger.error('Cannot rate yet - waiting for task ID');
      return;
    }

    const ratingKey = `${paraIndex}-${fbIndex}`;
    logger.info(`Rating comment ${ratingKey} as ${rating}`);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/results/${taskId}/update_ratings`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            commentRatings: {
              [ratingKey]: rating
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update rating');
      }

      // Update local state
      setCommentRatings(prev => {
        const newRatings = {
          ...prev,
          [ratingKey]: rating
        };
        logger.info('Updated commentRatings:', newRatings);
        return newRatings;
      });

      logger.info('Rating updated successfully');
    } catch (error) {
      logger.error('Rating error:', error);
    }
  };

  return {
    commentRatings,
    setCommentRatings,
    handleCommentRating
  };
} 